import { IGenericJSON } from '../helpers/interface';

/**
 * Type strings are namespaced by feature (`meeting.confirmed`,
 * `message.received`) and deliberately open
 */
export type NotificationType = string;

/**
 * Named AppNotification rather than Notification: the latter is a DOM global
 * (the browser's desktop-notification API), and shadowing it here would make
 * every reference ambiguous.
 */
export class AppNotification {
  id: number;
  type: NotificationType;
  actorId: number | null;

  /** What the notification points at, e.g. entityType 'meeting', entityId 42. */
  entityType: string;
  entityId: number;

  /** Snapshot captured at send time, so the text still renders if the entity changes. */
  data: Record<string, unknown> | null;

  readAt: Date | null;
  dismissedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;

  constructor(raw: IGenericJSON) {
    this.id = raw['id'];
    this.type = raw['type'];
    this.actorId = raw['actorId'] ?? null;

    this.entityType = raw['entityType'];
    this.entityId = raw['entityId'];

    this.data = raw['data'] ?? null;

    this.readAt = AppNotification.parseDate(raw['readAt']);
    this.dismissedAt = AppNotification.parseDate(raw['dismissedAt']);
    this.createdAt = AppNotification.parseDate(raw['createdAt']) ?? new Date();
    this.updatedAt = AppNotification.parseDate(raw['updatedAt']) ?? this.createdAt;
  }

  get isRead(): boolean {
    return this.readAt !== null;
  }

  /**
   * When to display as this notification's age. A collapsed notification
   * (see notify()'s groupKey handling) keeps its original createdAt forever
   * even as later events overwrite its content — updatedAt is when the
   * content actually being shown last changed, which is what "X minutes
   * ago" should mean from the viewer's perspective.
   */
  get displayTime(): Date {
    return this.updatedAt;
  }

  /** Reads a nullable timestamp, tolerating both a Date and an ISO string. */
  private static parseDate(value: unknown): Date | null {
    if (!value) return null;
    return value instanceof Date ? value : new Date(String(value));
  }

  public static digest(raw: IGenericJSON): AppNotification {
    return new AppNotification(raw);
  }

  public static digestResponse(data: IGenericJSON[]): AppNotification[] {
    return (data ?? []).map((row: IGenericJSON): AppNotification => this.digest(row));
  }
}
